import { useEffect } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Printer, X } from "lucide-react";

const rupiah = (v) => "Rp" + (Math.round(v || 0)).toLocaleString("id-ID");

function escapeHtml(s) {
  return String(s ?? "").replace(/[&<>"']/g, c => ({ "&":"&amp;", "<":"&lt;", ">":"&gt;", '"':"&quot;", "'":"&#39;" }[c]));
}

export function printOrder(orderLike, outlet) {
  doPrintFor(orderLike, outlet, "order");
}

export function printReceipt(tx, outlet) {
  doPrintFor(tx, outlet, "receipt");
}

function doPrintFor(tx, outlet, mode = "receipt") {
  if (!tx) return;
  const pc = outlet?.printer_config || {};
  const paper = pc.paper_size || "58";
  const paperSize = paper === "a4" ? "auto" : `${paper}mm auto`;
  const w = window.open("", "_blank", "width=380,height=640");
  if (!w) { alert("Popup diblokir. Izinkan popup untuk cetak."); return; }
  const timestamp = new Date(tx.created_at || Date.now()).toLocaleString("id-ID", {
    day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
  });
  const isOrder = mode === "order";
  const title = isOrder ? "KONFIRMASI PESANAN" : "STRUK PEMBAYARAN";
  const style = `
    @page { size: ${paperSize}; margin: 0; }
    * { box-sizing: border-box; }
    body { font-family: 'Courier New', ui-monospace, monospace; font-size: 11px; line-height: 1.35; margin: 0; padding: 6px 8px; color:#000; }
    .center { text-align: center; }
    .row { display: flex; justify-content: space-between; gap: 6px; }
    .row span:first-child { max-width: 60%; }
    hr { border: none; border-top: 1px dashed #000; margin: 4px 0; }
    .h { font-weight: 700; font-size: 13px; }
    .sm { font-size: 10px; color:#333; }
    .items div.item { margin-bottom: 3px; }
    .total { font-weight:700; font-size:12px; }
    .foot { margin-top: 6px; font-size: 10px; }
    .badge { display:inline-block; padding:2px 6px; border:1px dashed #000; margin: 2px 0; font-size: 10px; letter-spacing: 1px; }
    img.logo { max-width: 100%; max-height: 80px; margin: 4px 0; }
    @media print { body { padding: 4px 6px; } }
  `;
  const rows = tx.items.map(it => `
    <div class="item">
      <div>${escapeHtml(it.name)}</div>
      <div class="row"><span class="sm">${it.qty} x ${rupiah(it.price)}</span><span>${rupiah(it.subtotal || it.price * it.qty)}</span></div>
      ${it.notes ? `<div class="sm">Catatan: ${escapeHtml(it.notes)}</div>` : ""}
    </div>`).join("");
  const methodLabel = (tx.payment_method || "cash").toUpperCase();
  const cashLine = tx.payment_method === "cash" ? `
    <div class="row"><span>Tunai</span><span>${rupiah(tx.cash_received)}</span></div>
    <div class="row"><span>Kembalian</span><span>${rupiah(tx.change)}</span></div>` : "";
  const paySection = isOrder ? `
    <div class="center badge">*** BELUM DIBAYAR ***</div>
    <div class="center sm">Silakan konfirmasi ke kasir</div>
  ` : `
    <div class="row sm"><span>Metode</span><span>${methodLabel}</span></div>
    ${cashLine}
  `;
  const logo = pc.logo_url ? `<div class="center"><img class="logo" src="${escapeHtml(pc.logo_url)}"/></div>` : "";
  const headerExtra = pc.header_text ? `<div class="center sm">${escapeHtml(pc.header_text)}</div>` : "";
  const footerExtra = pc.footer_text ? `<div class="center sm">${escapeHtml(pc.footer_text)}</div>` : "";
  const html = `<!doctype html><html><head><meta charset="utf-8"><title>${escapeHtml(tx.trx_no)} ${isOrder ? "Order" : ""}</title><style>${style}</style></head><body>
    ${logo}
    <div class="center h">${escapeHtml(outlet?.brand || "MAZBOY")}</div>
    <div class="center sm">${escapeHtml(outlet?.name || "")}</div>
    <div class="center sm">${escapeHtml(outlet?.address || "")}</div>
    ${headerExtra}
    <div class="center badge">${title}</div>
    <hr/>
    <div class="row sm"><span>No.</span><span>${escapeHtml(tx.trx_no || "-")}</span></div>
    <div class="row sm"><span>Waktu</span><span>${timestamp}</span></div>
    <div class="row sm"><span>Kasir</span><span>${escapeHtml(tx.cashier_name || "-")}</span></div>
    <hr/>
    <div class="items">${rows}</div>
    <hr/>
    <div class="row"><span>Subtotal</span><span>${rupiah(tx.subtotal)}</span></div>
    ${tx.discount ? `<div class="row"><span>Diskon</span><span>-${rupiah(tx.discount)}</span></div>` : ""}
    <div class="row total"><span>TOTAL</span><span>${rupiah(tx.total)}</span></div>
    ${paySection}
    <hr/>
    <div class="center sm">${isOrder ? "Terima kasih atas pesanan Anda" : "Terima kasih"}</div>
    ${footerExtra}
    <div class="center foot">** MAZBOY HUB **</div>
    <script>window.onload=function(){window.print();setTimeout(function(){window.close();},500);};</script>
  </body></html>`;
  w.document.open(); w.document.write(html); w.document.close();
}

export function Receipt({ tx, outlet, onClose, autoOpen = false }) {
  useEffect(() => {
    if (!tx) return;
    const h = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [tx, onClose]);

  useEffect(() => {
    if (!tx || !autoOpen) return;
    const t = setTimeout(() => doPrintFor(tx, outlet, "receipt"), 250);
    return () => clearTimeout(t);
  }, [tx, outlet, autoOpen]);

  if (!tx) return null;

  const timestamp = new Date(tx.created_at || Date.now()).toLocaleString("id-ID", {
    day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
  });

  return (
    <Dialog open={!!tx} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-sm p-0 overflow-hidden">
        <div className="p-4 border-b flex items-center justify-between">
          <div>
            <div className="text-xs uppercase text-slate-500 tracking-wide">Struk Pembayaran</div>
            <div className="font-mono text-xs font-semibold">{tx.trx_no}</div>
          </div>
          <button data-testid="receipt-close" onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="w-4 h-4"/></button>
        </div>

        <div className="bg-white p-5 font-mono text-[12px] leading-relaxed text-slate-900" style={{ maxHeight: "60vh", overflow: "auto" }}>
          <div className="text-center font-bold text-sm">{outlet?.brand || "MAZBOY"}</div>
          <div className="text-center text-[10px] text-slate-600">{outlet?.name}</div>
          {outlet?.address && <div className="text-center text-[10px] text-slate-600">{outlet.address}</div>}
          <div className="border-t border-dashed border-slate-400 my-2"/>
          <div className="flex justify-between text-[10px]"><span>No.</span><span>{tx.trx_no}</span></div>
          <div className="flex justify-between text-[10px]"><span>Waktu</span><span>{timestamp}</span></div>
          <div className="flex justify-between text-[10px]"><span>Kasir</span><span>{tx.cashier_name}</span></div>
          <div className="border-t border-dashed border-slate-400 my-2"/>
          {tx.items.map((it, i) => (
            <div key={i} className="mb-1.5">
              <div className="truncate">{it.name}</div>
              <div className="flex justify-between text-[10px] text-slate-600">
                <span>{it.qty} x {rupiah(it.price)}</span>
                <span>{rupiah(it.subtotal || it.price * it.qty)}</span>
              </div>
            </div>
          ))}
          <div className="border-t border-dashed border-slate-400 my-2"/>
          <div className="flex justify-between"><span>Subtotal</span><span>{rupiah(tx.subtotal)}</span></div>
          {tx.discount > 0 && <div className="flex justify-between"><span>Diskon</span><span>-{rupiah(tx.discount)}</span></div>}
          <div className="flex justify-between font-bold text-sm mt-1"><span>TOTAL</span><span>{rupiah(tx.total)}</span></div>
          <div className="flex justify-between text-[10px] mt-1"><span>Metode</span><span>{(tx.payment_method || "").toUpperCase()}</span></div>
          {tx.payment_method === "cash" && (
            <>
              <div className="flex justify-between text-[10px]"><span>Tunai</span><span>{rupiah(tx.cash_received)}</span></div>
              <div className="flex justify-between text-[10px]"><span>Kembalian</span><span>{rupiah(tx.change)}</span></div>
            </>
          )}
          <div className="border-t border-dashed border-slate-400 my-2"/>
          <div className="text-center text-[11px]">Terima kasih</div>
          <div className="text-center text-[10px] text-slate-500">** MAZBOY HUB **</div>
        </div>

        <div className="p-3 border-t bg-slate-50 flex gap-2">
          <Button data-testid="receipt-close-btn" variant="outline" onClick={onClose} className="flex-1">Tutup</Button>
          <Button data-testid="receipt-print-btn" onClick={() => doPrintFor(tx, outlet, "receipt")} className="flex-1 bg-orange-500 hover:bg-orange-600">
            <Printer className="w-4 h-4 mr-2"/>Cetak Struk
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
